/**
 * stuburt-free-shipping-bar.js — <stuburt-ship-bar>
 *
 * Adds motion to the server-rendered free-shipping meter
 * (snippets/stuburt-free-shipping-bar.liquid). The golf ball ROLLS in from the
 * start to its current position whenever the meter becomes visible — when the
 * cart drawer opens (the drawer's `dialog:open` event) or when the /cart page
 * scrolls it into view (IntersectionObserver) — instead of snapping into place.
 * On a cart change it glides to the new spot. When the cart qualifies, the ball
 * pops into the cup (CSS handles the drop; this just toggles `.is-holed`).
 *
 * No client-side money: the resting position, the remaining amount and the
 * unlocked state are all rendered by Liquid. This element only reads the
 * already-computed progress percentage from `data-progress` and animates to it.
 * If the script never runs, the inline --stuburt-ship-progress keeps the ball
 * correctly placed (progressive enhancement).
 *
 * Custom Element; full teardown in disconnectedCallback (editor re-renders the
 * cart aggressively); prefers-reduced-motion respected (snaps, never rolls).
 */

// Kept in step with --stuburt-ship-roll in the snippet's CSS.
const ROLL_MS = 720;

const clamp = (n) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));

class StuburtShipBar extends HTMLElement {
  static get observedAttributes() {
    return ['data-progress'];
  }

  #target = 0;
  #reduce = false;
  #revealed = false;
  #abort = null;
  #rollAbort = null;
  #io = null;
  #drawer = null;
  #ball = null;
  #holeTimer = 0;
  #rollTimer = 0;

  attributeChangedCallback(name, _old, value) {
    if (name !== 'data-progress') return;
    this.#target = clamp(parseFloat(value));
    // Initial parse (before connect) is handled by connectedCallback. Once we're
    // connected and already shown, react to a cart change by gliding to the new
    // position (e.g. quantity changed in an open drawer).
    if (!this.isConnected || !this.#revealed) return;
    this.classList.remove('is-snapping');
    this.#set(this.#target);
    this.#scheduleSink();
  }

  connectedCallback() {
    this.#target = clamp(parseFloat(this.dataset.progress));
    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#abort = new AbortController();
    this.#ball = this.querySelector('.stuburt-ship__ball');
    // Mark as enhanced so CSS hands the resting/sunk state over to JS.
    this.classList.add('is-enhanced');

    if (this.#reduce) {
      // No motion: rest at the true position immediately.
      this.#reveal();
      return;
    }

    this.#drawer = this.closest('cart-drawer-component');

    if (this.#drawer) {
      // In the drawer: roll on every open, re-arm on close.
      if (this.#drawer.querySelector('dialog')?.open) {
        this.#reveal(); // already open (e.g. re-rendered while open) — show in place
      } else {
        this.#arm();
      }
      this.#drawer.addEventListener('dialog:open', this.#rollIn, { signal: this.#abort.signal });
      this.#drawer.addEventListener('dialog:close', this.#arm, { signal: this.#abort.signal });
    } else {
      // On the cart page: roll when scrolled into view; re-arm when it leaves.
      this.#arm();
      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.#rollIn();
            else this.#arm();
          }
        },
        { threshold: 0.35 }
      );
      this.#io.observe(this);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#holeTimer);
    window.clearTimeout(this.#rollTimer);
    this.#io?.disconnect();
    this.#io = null;
    this.#abort?.abort();
    this.#abort = null;
    this.#rollAbort?.abort();
    this.#rollAbort = null;
  }

  get #qualified() {
    return this.classList.contains('stuburt-ship--qualified');
  }

  /** Park the ball at the start, ready to roll, without animating. */
  #arm = () => {
    window.clearTimeout(this.#holeTimer);
    window.clearTimeout(this.#rollTimer);
    this.#rollAbort?.abort();
    this.#revealed = false;
    this.classList.add('is-snapping');
    this.classList.remove('is-rolling', 'is-holed');
    this.#set(0);
    void this.offsetWidth; // commit the 0% with no transition
  };

  /** Show the ball at its true position with no roll (already-open / reduced motion). */
  #reveal() {
    this.#revealed = true;
    this.classList.add('is-snapping');
    this.classList.remove('is-rolling');
    this.#set(this.#target);
    this.classList.toggle('is-holed', this.#qualified);
    void this.offsetWidth;
    this.classList.remove('is-snapping');
  }

  /** Roll the ball from the start to its current position, then sink the putt. */
  #rollIn = () => {
    if (this.#revealed) return; // don't replay while already in view
    this.#revealed = true;
    window.clearTimeout(this.#holeTimer);
    window.clearTimeout(this.#rollTimer);

    // Start from 0 with no transition...
    this.classList.add('is-snapping');
    this.classList.remove('is-holed');
    this.#set(0);
    void this.offsetWidth;

    // ...then release the brake and roll to the target.
    this.classList.remove('is-snapping');
    this.classList.add('is-rolling');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.#set(this.#target));
    });

    this.#rollTimer = window.setTimeout(() => this.classList.remove('is-rolling'), ROLL_MS + 360);

    // Drop into the cup only once the ball has actually reached the pin.
    this.#scheduleSink();
  };

  /**
   * Drop the ball into the cup — but ONLY once it has actually reached the pin,
   * keyed off the end of its `left` travel, so it never vanishes mid-move. Used by
   * both the roll-in and a glide after a cart change (e.g. an add that crosses the
   * threshold). A timer backs up transitionend in case it doesn't fire.
   */
  #scheduleSink() {
    this.#rollAbort?.abort();
    window.clearTimeout(this.#holeTimer);

    if (!this.#qualified) {
      this.classList.remove('is-holed');
      return;
    }
    if (this.classList.contains('is-holed')) return; // already dropped in

    const sink = () => {
      if (this.#qualified) this.classList.add('is-holed');
    };
    if (this.#reduce) {
      sink();
      return;
    }

    this.#rollAbort = new AbortController();
    this.#ball?.addEventListener(
      'transitionend',
      (event) => {
        if (event.propertyName === 'left') sink();
      },
      { signal: this.#rollAbort.signal, once: true }
    );
    this.#holeTimer = window.setTimeout(sink, ROLL_MS + 180);
  }

  #set(pct) {
    this.style.setProperty('--stuburt-ship-progress', `${pct}%`);
  }
}

if (!customElements.get('stuburt-ship-bar')) {
  customElements.define('stuburt-ship-bar', StuburtShipBar);
}
